import {
  ComplianceStatus,
  IDischargeIntelligenceSummary,
  IFacilityDischargeData,
  IOutfall,
  IParameterLimitRow,
  TrendDirection,
} from '../types/discharge';

const MONTH_MAP: Record<string, number> = {
  JAN: 0,
  FEB: 1,
  MAR: 2,
  APR: 3,
  MAY: 4,
  JUN: 5,
  JUL: 6,
  AUG: 7,
  SEP: 8,
  OCT: 9,
  NOV: 10,
  DEC: 11,
};

function parseEchoDate(dateStr: string): Date {
  const parts = dateStr.split('-');
  if (parts.length !== 3) return new Date(0);
  const [day, mon, yr] = parts;
  const month = MONTH_MAP[mon.toUpperCase()];
  if (month === undefined) return new Date(0);
  return new Date(2000 + parseInt(yr, 10), month, parseInt(day, 10));
}

function formatEchoDate(dateStr: string): string {
  const d = parseEchoDate(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// Radius each facility's outfalls orbit around their facility center
const OUTFALL_ORBIT_RADIUS_M = 40;
// Radius facilities orbit around the single provided lat/lon when there are multiple
const FACILITY_ORBIT_RADIUS_M = 100;
const METERS_PER_DEGREE_LAT = 111111;

function computeOffset(
  centerLat: number,
  centerLon: number,
  index: number,
  total: number,
  radiusM: number
): { lat: number; lon: number } {
  const angle = (index / total) * 2 * Math.PI;
  const latPerM = 1 / METERS_PER_DEGREE_LAT;
  const lonPerM = 1 / (METERS_PER_DEGREE_LAT * Math.cos((centerLat * Math.PI) / 180));
  return {
    lat: centerLat + radiusM * latPerM * Math.cos(angle),
    lon: centerLon + radiusM * lonPerM * Math.sin(angle),
  };
}

function parsePct(pctStr: string | null | undefined): number | null {
  if (pctStr == null) return null;
  const n = parseFloat(pctStr.replace('%', ''));
  return isNaN(n) ? null : n;
}

export function transformDischargeData(
  raw: any,
  facilityLat: number,
  facilityLon: number,
  facilityIndex: number,
  totalFacilities: number,
  receivingWater = '[Receiving water unavailable]',
  huc12 = '[HUC-12 unavailable]',
  outfallLocations?: Record<string, { lat: number; lon: number }>
): IFacilityDischargeData {
  const results = raw.Results;
  const facilityName: string = results.CWPName ?? '';
  const permFeatures: any[] = results.PermFeatures || [];
  const n = permFeatures.length;

  // When multiple facilities exist, place this facility's center on an outer orbit
  const facilityCenterLat =
    totalFacilities > 1
      ? computeOffset(facilityLat, facilityLon, facilityIndex, totalFacilities, FACILITY_ORBIT_RADIUS_M).lat
      : facilityLat;
  const facilityCenterLon =
    totalFacilities > 1
      ? computeOffset(facilityLat, facilityLon, facilityIndex, totalFacilities, FACILITY_ORBIT_RADIUS_M).lon
      : facilityLon;

  // Find most recent MonitoringPeriodEndDate across entire dataset
  let mostRecentDateObj = new Date(0);
  permFeatures.forEach((pf) =>
    pf.Parameters.forEach((p: any) =>
      p.DischargeMonitoringReports.forEach((r: any) => {
        if (r.MonitoringPeriodEndDate) {
          const d = parseEchoDate(r.MonitoringPeriodEndDate);
          if (d > mostRecentDateObj) mostRecentDateObj = d;
        }
      })
    )
  );

  const cutoff = new Date(mostRecentDateObj);
  cutoff.setMonth(cutoff.getMonth() - 12);

  const outfalls: IOutfall[] = permFeatures.map((pf, i) => {
    const outfallOffset = computeOffset(facilityCenterLat, facilityCenterLon, i, n, OUTFALL_ORBIT_RADIUS_M);

    let outfallExceedances12mo = 0;
    let outfallLastExceedanceDateObj: Date | null = null;
    let outfallLastExceedanceDate: string | null = null;
    let outfallLastExceedanceParam: string | null = null;
    let outfallLastExceedancePct: number | null = null;

    const parameters: IParameterLimitRow[] = [];

    pf.Parameters.forEach((param: any) => {
      const allRows: any[] = param.DischargeMonitoringReports;

      // Group rows by (StatisticalBaseCode:LimitValueTypeCode) — one group per limit type
      const groups = new Map<string, any[]>();
      allRows.forEach((row: any) => {
        const key = `${row.StatisticalBaseCode}:${row.LimitValueTypeCode}`;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key)!.push(row);
      });

      groups.forEach((rows) => {
        // Sort ascending by reporting period
        rows.sort(
          (a, b) =>
            parseEchoDate(a.MonitoringPeriodEndDate).getTime() -
            parseEchoDate(b.MonitoringPeriodEndDate).getTime()
        );

        const last12Rows = rows.filter((r) => parseEchoDate(r.MonitoringPeriodEndDate) >= cutoff);

        // Most recent non-NODI row as the "current" reported value
        const valueRows = rows.filter((r) => r.NODICode == null && r.DMRValueNmbr != null);
        const currentRow = valueRows.length > 0 ? valueRows[valueRows.length - 1] : null;
        const isNoDischarge = currentRow == null || rows[rows.length - 1].NODICode != null;

        const currentValue = currentRow != null ? parseFloat(currentRow.DMRValueNmbr) : null;

        // Use most recent row for the limit fields (current active limit)
        const latestRow = rows[rows.length - 1];
        const limitValue =
          latestRow.LimitValueNmbr != null ? parseFloat(latestRow.LimitValueNmbr) : null;

        // Sparkline: last 12 months non-NODI values, oldest→newest
        const sparklineValues = last12Rows
          .filter((r) => r.NODICode == null && r.DMRValueNmbr != null)
          .map((r) => parseFloat(r.DMRValueNmbr))
          .filter((v) => !isNaN(v));

        // Trend from last two sparkline values
        let trend: TrendDirection = 'flat';
        if (sparklineValues.length >= 2) {
          const last = sparklineValues[sparklineValues.length - 1];
          const prev = sparklineValues[sparklineValues.length - 2];
          trend = last > prev ? 'up' : last < prev ? 'down' : 'flat';
        }

        // Exceedances in the 12-month window
        const exceedanceRows = last12Rows.filter((r) => r.ExceedencePct != null);
        const exceedanceCount12mo = exceedanceRows.length;

        let lastExceedanceDate: string | null = null;
        let lastExceedancePct: number | null = null;
        if (exceedanceRows.length > 0) {
          const lastExRow = exceedanceRows[exceedanceRows.length - 1];
          lastExceedanceDate = formatEchoDate(lastExRow.MonitoringPeriodEndDate);
          lastExceedancePct = parsePct(lastExRow.ExceedencePct);

          // Track outfall-level last exceedance
          const exDateObj = parseEchoDate(lastExRow.MonitoringPeriodEndDate);
          if (!outfallLastExceedanceDateObj || exDateObj > outfallLastExceedanceDateObj) {
            outfallLastExceedanceDateObj = exDateObj;
            outfallLastExceedanceDate = formatEchoDate(lastExRow.MonitoringPeriodEndDate);
            outfallLastExceedanceParam = param.ParameterDesc;
            outfallLastExceedancePct = lastExceedancePct;
          }
        }

        outfallExceedances12mo += exceedanceCount12mo;

        // Violations from exceedance rows in the window
        const violations = exceedanceRows.flatMap((r: any) =>
          (r.NPDESViolations || []).map((v: any) => ({
            code: v.ViolationCode,
            desc: v.ViolationDesc,
            severityDesc: v.ViolationSeverityDesc,
          }))
        );

        const percentOfLimit =
          currentValue != null && limitValue != null && limitValue !== 0
            ? Math.round((currentValue / limitValue) * 1000) / 10
            : null;

        parameters.push({
          parameterCode: param.ParameterCode,
          parameterName: param.ParameterDesc,
          monitoringLocationDesc: param.MonitoringLocationDesc,
          limitType: latestRow.StatisticalBaseDesc,
          limitTypeDesc: latestRow.StatisticalBaseTypeDesc,
          limitQualifier: latestRow.LimitValueQualifierCode,
          limitValue,
          limitUnit: latestRow.LimitUnitDesc,
          currentValue,
          currentValueUnit: currentRow?.DMRUnitDesc ?? latestRow.LimitUnitDesc,
          currentValueQualifier: currentRow?.DMRValueQualifierCode ?? '=',
          percentOfLimit,
          trend,
          sparklineValues,
          exceedanceCount12mo,
          lastExceedanceDate,
          lastExceedancePct,
          isNoDischarge,
          violations,
        });
      });
    });

    const complianceStatus: ComplianceStatus =
      outfallExceedances12mo === 0 ? 'green' : outfallExceedances12mo <= 2 ? 'amber' : 'red';

    const real = outfallLocations?.[pf.PermFeatureNmbr];
    const markerLat = real ? real.lat : outfallOffset.lat;
    const markerLon = real ? real.lon : outfallOffset.lon;

    return {
      id: pf.PermFeatureNmbr,
      facilityName,
      typeCode: pf.PermFeatureTypeCode,
      typeDesc: pf.PermFeatureTypeDesc,
      markerLat,
      markerLon,
      complianceStatus,
      exceedanceCount12mo: outfallExceedances12mo,
      lastExceedanceDate: outfallLastExceedanceDate,
      lastExceedanceParam: outfallLastExceedanceParam,
      lastExceedancePct: outfallLastExceedancePct,
      receivingWater,
      huc12,
      parameters,
    };
  });

  // Summary aggregations
  const totalExceedances12mo = outfalls.reduce((s, o) => s + o.exceedanceCount12mo, 0);

  const paramExceedMap = new Map<string, number>();
  outfalls.forEach((o) =>
    o.parameters.forEach((p) => {
      if (p.exceedanceCount12mo > 0) {
        paramExceedMap.set(
          p.parameterName,
          (paramExceedMap.get(p.parameterName) ?? 0) + p.exceedanceCount12mo
        );
      }
    })
  );
  let mostExceededParam: string | null = null;
  let maxParamCount = 0;
  paramExceedMap.forEach((count, name) => {
    if (count > maxParamCount) {
      maxParamCount = count;
      mostExceededParam = name;
    }
  });

  const topOutfall = outfalls.reduce<IOutfall | null>(
    (best, o) => (!best || o.exceedanceCount12mo > best.exceedanceCount12mo ? o : best),
    null
  );

  let globalLastExceedanceDate: string | null = null;
  outfalls
    .filter((o) => o.lastExceedanceDate != null)
    .forEach((o) => {
      if (globalLastExceedanceDate === null) {
        globalLastExceedanceDate = o.lastExceedanceDate;
      }
    });

  const summary: IDischargeIntelligenceSummary = {
    totalOutfalls: outfalls.length,
    totalParameterRows: outfalls.reduce((s, o) => s + o.parameters.length, 0),
    totalExceedances12mo,
    mostExceededParam,
    mostExceededOutfall: topOutfall && topOutfall.exceedanceCount12mo > 0 ? topOutfall.id : null,
    lastExceedanceDate: globalLastExceedanceDate,
    mostRecentDMRPeriod:
      mostRecentDateObj > new Date(0)
        ? mostRecentDateObj.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
        : null,
  };

  return {
    npdesId: results.SourceId,
    facilityName,
    facilityLat: Number.isFinite(facilityLat) ? facilityLat : null,
    facilityLon: Number.isFinite(facilityLon) ? facilityLon : null,
    outfalls,
    summary,
  };
}
